import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowRight01Icon,
  Edit02Icon,
  RedoIcon,
  SquareLock02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import moment from "moment";
import { Show } from "shared/components";
import { cx } from "shared/utils";
import {
  LeadActivity,
  LeadActivitySource,
  LeadActivityType
} from "views/leads-id/types";
import { TimeLineIcon } from "views/leads-id/slices/Details/slices/ActivityLog/components/TimeLine/TimeLineIcon";
import { TimeLineDescriptionItem } from "views/leads-id/slices/Details/slices/ActivityLog/components/TimeLine/TimeLineDescriptionItem";
import { getActorAttribution } from "views/leads-id/slices/Details/slices/ActivityLog/components/TimeLine/activity-attribution";
import { ChangeRow } from "views/properties-id/slices/ActivityLog/components/ActivityCard";
import { UnitActivityRow } from "./controller";

const LOCKBOX_BUNDLE_TYPE = "lockbox_code_generated";

const auditIcon = (row: UnitActivityRow) => {
  if (row.bundle_type === LOCKBOX_BUNDLE_TYPE) return SquareLock02Icon;
  if (row.actor_type === "pmi_sync") return RedoIcon;
  return Edit02Icon;
};

const toLeadActivity = (
  row: UnitActivityRow,
  eventType: LeadActivityType
): LeadActivity => ({
  id: row.activity_log_id ?? 0,
  source: LeadActivitySource.ACTIVITY,
  activity_type: eventType,
  title: row.summary ?? "",
  description: row.description ?? null,
  actor_type: row.actor_type ?? "",
  actor_name: row.actor_name ?? "",
  metadata: row.metadata ?? {},
  occurred_at: row.occurred_at ?? "",
  created_at: row.occurred_at ?? "",
  updated_at: row.occurred_at ?? ""
});

const AuditDescription = ({ row }: { row: UnitActivityRow }) => {
  const changes = row.changes ?? [];
  if (!changes.length) return null;

  return (
    <div className="space-y-1">
      {changes.map((change) => (
        <ChangeRow
          key={`${row.id}-${change.field}-${change.unit?.id ?? "self"}`}
          change={change}
        />
      ))}
    </div>
  );
};

export const ActivityRow = ({
  row,
  onSelect
}: {
  row: UnitActivityRow;
  onSelect: (row: UnitActivityRow) => void;
}) => {
  const isUnread = row.read === false;
  const isClickable = Boolean(row.navigate_to);
  const activity = row.event_type ? toLeadActivity(row, row.event_type) : null;

  return (
    <div
      className={cx(
        "relative border-b border-alpha-40",
        isClickable ? "hover:bg-background-900" : ""
      )}
    >
      <Show if={isClickable}>
        <button
          type="button"
          aria-label={row.summary ?? "Activity"}
          onClick={() => onSelect(row)}
          className="absolute inset-0"
        />
      </Show>

      <div className="pointer-events-none relative flex items-center gap-3.5 px-6 py-4">
        <span className="flex w-2.5 shrink-0 justify-center pt-1">
          <Show if={isUnread}>
            <span className="size-1.5 rounded-full bg-primary-500" />
          </Show>
        </span>

        {activity ? (
          <TimeLineIcon
            activityType={activity.activity_type}
            actorType={activity.actor_type}
            metadata={activity.metadata}
          />
        ) : (
          <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-background-800">
            <HugeiconsIcon
              size={20}
              strokeWidth={1.5}
              icon={auditIcon(row)}
              className="text-content-1000"
            />
          </span>
        )}

        <div className="min-w-0 flex-1 space-y-2 pt-1">
          <p className="text-sm font-bold text-content-1000">
            {row.summary}
            {activity ? getActorAttribution(activity) : ""}
            <Show if={Boolean(row.lead?.name)}>
              <span className="font-normal text-content-500">
                {" · "}
                {row.lead?.name}
              </span>
            </Show>
          </p>
          <div className="pointer-events-auto">
            {activity ? (
              <TimeLineDescriptionItem activity={activity} />
            ) : (
              <AuditDescription row={row} />
            )}
          </div>
        </div>

        <span className="shrink-0 pt-1 text-sm text-content-300">
          {row.occurred_at ? moment(row.occurred_at).fromNow() : ""}
        </span>

        <Show if={isClickable}>
          <HugeiconsIcon
            size={20}
            icon={ArrowRight01Icon}
            className="mt-1.5 shrink-0 text-content-300"
          />
        </Show>
      </div>
    </div>
  );
};
